import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
      converter: {
        toAttribute(value) {
          let retVal = String(value)
          return retVal
        },

        fromAttribute(value) {
          let retVal = Boolean(value == 'true')
          return retVal
        },
      },
    },
    lockBody: {
      type: Boolean,
      attribute: 'lock-body',
      converter: {
        toAttribute(value) {
          let retVal = String(value)
          return retVal
        },

        fromAttribute(value) {
          let retVal = Boolean(value == 'true')
          return retVal
        },
      },
    },
    fixIosScroll: {
      type: Boolean,
      attribute: 'fix-ios-scroll',
      converter: {
        toAttribute(value) {
          let retVal = String(value)
          return retVal
        },

        fromAttribute(value) {
          let retVal = Boolean(value == 'true')
          return retVal
        },
      },
    },
    removeClose: {
      type: Boolean,
      attribute: 'remove-close',
      converter: {
        toAttribute(value) {
          let retVal = String(value)
          return retVal
        },

        fromAttribute(value) {
          let retVal = Boolean(value == 'true')
          return retVal
        },
      },
    },
  }

  constructor() {
    super()
  }

  preventDefault(e) {
    e.preventDefault()
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      let supportsPassive = false
      try {
        window.addEventListener(
          'test',
          null,
          Object.defineProperty({}, 'passive', {
            get: function () {
              supportsPassive = true
            },
          })
        )
      } catch (e) {}
      const wheelOpt = supportsPassive ? { passive: false } : false
      if (this.open && this.lockBody) {
        if (this.fixIosScroll) {
          window.addEventListener('touchmove', this.preventDefault, wheelOpt) // mobile
        }
        document.documentElement.classList.add('overflow-hidden-modal')
      } else {
        document.documentElement.classList.remove('overflow-hidden-modal')
        if (this.fixIosScroll) {
          window.removeEventListener('touchmove', this.preventDefault, wheelOpt)
        }
      }
    }
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver?.disconnect()
  }

  closeFromModal(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.open = false
    }
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }
  }

  close(event) {
    if (event.type === 'click') {
      this.open = false
      return
    }
    event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      ${!this.removeClose == true
        ? `<button
        @click="${this.close}"
        class="button"
        type="button"
        aria-label="${t('accessibility.close')}"
      >
        ${unsafeHTML(paths.close)}
      </button>`
        : ''}
      <div @click="${this.close}" class="backface"></div>
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}">
          <slot @click="${this.closeFromModal}"></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      width: 4rem;
      position: fixed;
      top: 4rem;
      right: 4rem;
      display: flex;
      background-color: var(--hh-background);
      border: 0.1rem solid var(--hh-border-color);
      border-radius: 50%;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: 1.2rem;
      z-index: 2;
    }

    .backface {
      width: 100vw;
      height: 100vh;
      background-color: rgba(0, 0, 0, 0.3);
      position: absolute;
      top: 0;
      left: 0;
      z-index: 0;
      display: block !important;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: auto;
      width: 100%;
      padding: var(--hh-padding);
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)
